import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { FiCopy, FiLock, FiUnlock, FiUsers } from 'react-icons/fi';
import { cGroup, cJoinGroup, fullUrl } from '../../api';
import { useCommunity } from '../../contexts/CommunityContext';
import { useLanguage } from '../../i18n';
import GroupChat from '../../components/community/GroupChat';
import GroupMembers from '../../components/community/GroupMembers';
import GroupSettings from '../../components/community/GroupSettings';
import { BLUE, BLUE_GRADIENT, compactNumber } from '../../components/community/brand';

/**
 * One group: header (logo, nickname, public/private, my role, members) + the
 * three tabs — **chat**, **members** (change admin, nickname, remove) and
 * **settings** for the admins (name, logo, public/private, invite link, delete).
 */
export default function GroupPage() {
  const { id } = useParams();
  const { t } = useLanguage();
  const { user, requireAuth, openAuth } = useCommunity();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [tab, setTab] = useState('chat');
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    cGroup(id)
      .then(setGroup)
      .catch((err) => toast.error(err?.response?.data?.detail || t('somethingWrong')))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [id, user]);

  const join = async () => {
    if (!requireAuth(t('loginRequiredDesc'))) return;
    try {
      await cJoinGroup(group.id);
      toast.success(t('groupJoined'));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('somethingWrong'));
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(group.invite_link);
      toast.success(t('linkCopied'));
    } catch (e) { toast(group.invite_link); }
  };

  if (loading && !group) {
    return <p className="mx-auto max-w-[1100px] px-4 py-6 text-sm text-gray-400">{t('loading')}</p>;
  }
  if (!group) {
    return (
      <div className="mx-auto max-w-[1100px] px-4 py-10 text-center">
        <p className="text-sm text-gray-500">{t('groupPrivateHint')}</p>
        <button
          type="button"
          onClick={() => navigate('/groups')}
          className="mt-3 font-bold"
          style={{ color: BLUE }}
        >
          {t('groupsNav')}
        </button>
      </div>
    );
  }

  const isMember = !!group.is_member;
  const canManage = !!group.can_manage;
  const roleLabel = group.my_role === 'owner' ? t('groupRoleOwner')
    : group.my_role === 'admin' ? t('groupRoleAdmin') : t('groupRoleMember');

  const tabBtn = (key, label) => (
    <button
      key={key}
      type="button"
      onClick={() => setTab(key)}
      className={`px-4 py-2 rounded-full text-xs sm:text-sm font-bold ${tab === key ? 'text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'}`}
      style={tab === key ? { background: BLUE } : undefined}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-[1100px] px-3 lg:px-4 py-4">
      <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
        <div
          className="h-24"
          style={{ background: group.cover ? `url(${fullUrl(group.cover)}) center/cover` : BLUE_GRADIENT }}
        />
        <div className="px-4 pb-4">
          <div className="flex items-end gap-3 -mt-9">
            {group.avatar ? (
              <img
                src={fullUrl(group.avatar)}
                alt={group.name}
                className="w-[72px] h-[72px] rounded-2xl object-cover ring-4 ring-white dark:ring-gray-900"
              />
            ) : (
              <span
                className="w-[72px] h-[72px] rounded-2xl flex items-center justify-center text-white text-2xl font-extrabold ring-4 ring-white dark:ring-gray-900"
                style={{ background: BLUE }}
              >
                {(group.name || 'G').slice(0, 1).toUpperCase()}
              </span>
            )}
            <div className="min-w-0 flex-1 pb-1">
              <h1 className="font-extrabold text-lg truncate flex items-center gap-2">
                <span className="truncate">{group.name}</span>
                {group.is_public ? (
                  <FiUnlock className="w-4 h-4 text-emerald-600 shrink-0" title={t('groupPublic')} />
                ) : (
                  <FiLock className="w-4 h-4 text-gray-400 shrink-0" title={t('groupPrivate')} />
                )}
              </h1>
              <p className="text-[11px] text-gray-500 truncate">
                <FiUsers className="w-3 h-3 inline" /> {compactNumber(group.member_count)} {t('groupMembers')}
                {isMember && <> · <span className="font-bold">{roleLabel}</span></>}
              </p>
            </div>
            {!isMember && group.is_public && (
              <button
                type="button"
                onClick={() => (user ? join() : openAuth('signin', t('loginRequiredDesc')))}
                className="mb-1 px-4 py-2 rounded-full text-sm font-bold text-white"
                style={{ background: BLUE }}
              >
                {t('groupJoinOpen')}
              </button>
            )}
          </div>

          {group.about && (
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-line">{group.about}</p>
          )}

          {group.invite_link && (
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[11px] font-bold text-gray-500">{t('groupInviteCode')}:</span>
              <code className="text-[11px] px-2 py-1 rounded-lg bg-gray-100 dark:bg-gray-800">
                {group.invite_code}
              </code>
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold text-white"
                style={{ background: BLUE }}
              >
                <FiCopy className="w-3.5 h-3.5" /> {t('groupCopyLink')}
              </button>
            </div>
          )}
        </div>
      </section>

      {!isMember ? (
        <p className="mt-3 text-sm text-gray-500">{t('groupPrivateHint')}</p>
      ) : (
        <>
          <div className="flex items-center gap-2 my-3 flex-wrap">
            {tabBtn('chat', t('groupChat'))}
            {tabBtn('members', t('groupMembers'))}
            {canManage && tabBtn('settings', t('groupSettings'))}
          </div>

          <section className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-3 sm:p-4">
            {tab === 'chat' && <GroupChat groupId={group.id} canManage={canManage} />}
            {tab === 'members' && (
              <GroupMembers groupId={group.id} myRole={group.my_role} canInvite={!!group.can_invite} />
            )}
            {tab === 'settings' && canManage && (
              <GroupSettings
                group={group}
                onUpdated={(g) => setGroup((prev) => ({ ...prev, ...g }))}
                onLeft={() => navigate('/groups')}
                onDeleted={() => navigate('/groups')}
              />
            )}
          </section>
        </>
      )}
    </div>
  );
}
